import React from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';

const ViewFamilleTypeArticleModal = ({ isOpen, familleTypeArticle, onClose }) => {
  const { isDarkMode } = useTheme();

  if (!isOpen || !familleTypeArticle) return null;

  // Always neutral colors in dark mode
  const getFamilleColor = (designation) => {
    if (isDarkMode) return 'bg-gray-700 text-gray-200 border-gray-600';
    const colorMap = {
      'RIA': 'bg-blue-100 text-blue-800 border-blue-200',
      'Extincteur': 'bg-red-100 text-red-800 border-red-200',
      'PIA': 'bg-green-100 text-green-800 border-green-200',
      'AUTO': 'bg-purple-100 text-purple-800 border-purple-200',
      'UMM': 'bg-orange-100 text-orange-800 border-orange-200'
    };
    return colorMap[designation] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getFamilleIcon = (designation) => {
    const iconMap = {
      'RIA': 'fas fa-fire-extinguisher',
      'Extincteur': 'fas fa-spray-can', 
      'PIA': 'fas fa-shield-alt',
      'AUTO': 'fas fa-cog',
      'UMM': 'fas fa-tools'
    };
    return iconMap[designation] || 'fas fa-tag';
  };

  const getFamilleDescription = (designation) => {
    const descriptionMap = {
      'RIA': 'Robinet d\'Incendie Armé - Équipement fixe de lutte contre l\'incendie',
      'Extincteur': 'Extincteurs portables et mobiles - Appareils de première intervention',
      'PIA': 'Poteau d\'Incendie - Points d\'eau pour les services de secours',
      'AUTO': 'Systèmes automatiques - Installation de protection automatique',
      'UMM': 'Unité Mobile de Manutention - Équipements mobiles spécialisés'
    };
    return descriptionMap[designation] || 'Famille de type d\'article';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`px-6 py-4 ${isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-r from-gray-600 to-gray-700'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mr-4">
                <i className={`${getFamilleIcon(familleTypeArticle.designation)} text-white text-xl`}></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{familleTypeArticle.designation}</h2>
                <p className="text-white text-opacity-90 text-sm">
                  Famille de type d'article • ID: {familleTypeArticle.id}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(95vh-100px)]">
          <div className="p-6 space-y-6">
            {/* Main Information Card */}
            <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center">
                  <div className={`p-3 ${isDarkMode ? 'bg-gray-800' : 'bg-primary-100'} rounded-xl mr-4`}>
                    <i className={`fas fa-info-circle text-xl ${isDarkMode ? 'text-gray-400' : 'text-primary-600'}`}></i>
                  </div>
                  <div>
                    <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Informations générales</h3>
                    <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Détails de la famille de type d'article</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div className={`rounded-lg p-4 border ${
                    isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                  }`}>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Identifiant</label>
                    <div className="flex items-center">
                      <i className="fas fa-hashtag text-gray-400 mr-2"></i>
                      <span className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>#{familleTypeArticle.id}</span>
                    </div>
                  </div>

                  <div className={`rounded-lg p-4 border ${
                    isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                  }`}>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Désignation</label>
                    <div className="flex items-center">
                      <i className={`${getFamilleIcon(familleTypeArticle.designation)} text-gray-400 mr-2`}></i>
                      <span className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{familleTypeArticle.designation}</span>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div className={`rounded-lg p-4 border ${
                    isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                  }`}>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Type de famille</label>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${isDarkMode ? 'bg-gray-700 text-gray-200 border-gray-600' : getFamilleColor(familleTypeArticle.designation)}`}>
                      <div className="w-2 h-2 rounded-full bg-current mr-2"></div>
                      Famille de type d'article
                    </span>
                  </div>

                  <div className={`rounded-lg p-4 border ${
                    isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                  }`}>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Description</label>
                    <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-900'} leading-relaxed`}>
                      {getFamilleDescription(familleTypeArticle.designation)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Visual Preview */}
            <div className={`${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} rounded-xl border p-6`}>
              <div className="flex items-center mb-6">
                <div className={`p-3 ${isDarkMode ? 'bg-gray-800' : 'bg-primary-100'} rounded-xl mr-4`}>
                  <i className={`fas fa-eye text-xl ${isDarkMode ? 'text-gray-400' : 'text-primary-600'}`}></i>
                </div>
                <div>
                  <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Aperçu visuel</h3>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Rendu de la famille dans l'interface</p>
                </div>
              </div>

              {/* Card Preview */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg p-6`}>
                <h4 className={`text-sm font-medium mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Aperçu en mode carte</h4>
                <div className="max-w-sm">
                  <div className={`${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} rounded-lg border p-4 shadow-sm`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-3 ${isDarkMode ? 'bg-gray-700 text-gray-200 border-gray-600' : getFamilleColor(familleTypeArticle.designation)}`}>
                          <i className={`${getFamilleIcon(familleTypeArticle.designation)} text-lg`}></i>
                        </div>
                        <div>
                          <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {familleTypeArticle.designation}
                          </h3>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>ID: {familleTypeArticle.id}</p>
                        </div>
                      </div>
                    </div>
                    <div className="mb-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${isDarkMode ? 'bg-gray-700 text-gray-200 border-gray-600' : getFamilleColor(familleTypeArticle.designation)}`}>
                        <div className="w-2 h-2 rounded-full bg-current mr-1.5"></div>
                        Famille de type
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`border-t px-6 py-4 ${
          isDarkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className={`px-6 py-2 rounded-lg transition-colors font-medium ${
                isDarkMode 
                  ? 'bg-gray-600 text-white hover:bg-gray-700' 
                  : 'bg-gray-600 text-white hover:bg-gray-700'
              }`}
            >
              <i className="fas fa-times mr-2"></i>
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewFamilleTypeArticleModal;